import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiExternalLink, FiGithub, FiTrendingUp, FiBarChart, FiDollarSign, FiPieChart, FiDatabase } from 'react-icons/fi';

const ProjectsContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 0 20px;
`;

const Title = styled(motion.h1)`
  font-family: 'Playfair Display', serif;
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #e2e8f0;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const GitHubNote = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 1.5rem;
  margin: 0 auto 3rem;
  max-width: 800px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  
  p {
    color: #f7fafc;
    font-size: 1.1rem;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  a {
    color: #90cdf4;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      color: #bee3f8;
    }
  }
`;

const ProjectsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 15px;
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }
`;

const ProjectIcon = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  color: white;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
`;

const ProjectDescription = styled.p`
  color: #e2e8f0;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TechTag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProjectLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
  }
`;

const Projects = () => {
  const projects = [
    {
      id: 1,
      title: "NYC-Taxi-SQL",
      description: "This project explores and analyzes the NYC Taxi Trip dataset hosted on Google BigQuery using SQL. It includes data exploration queries, data cleaning scripts, and insights — all written, tested, and documented for comprehensive analysis of taxi trip patterns and trends.",
      icon: <FiDatabase />,
      techStack: ["SQL", "Google BigQuery", "Data Analysis", "Data Cleaning", "Statistical Analysis"],
      demoLink: "https://github.com/Shardul1998/NYC-Taxi-SQL",
      githubLink: "https://github.com/Shardul1998/NYC-Taxi-SQL",
      category: "Data Analysis"
    },
    {
      id: 2,
      title: "Sales Performance Dashboard",
      description: "Executive Sales Performance Dashboard (2018–2021) - An interactive business intelligence dashboard analyzing sales performance across regions, years, customer segments, and product sub-categories, designed to provide executive-level insights for data-driven decision making.",
      icon: <FiBarChart />,
      techStack: ["Dashboard Design", "Business Intelligence", "Data Visualization", "KPI Analysis", "Executive Reporting"],
      demoLink: "https://github.com/Shardul1998/Sales-performance-dashboard",
      githubLink: "https://github.com/Shardul1998/Sales-performance-dashboard",
      category: "Business Intelligence"
    },
    {
      id: 3,
      title: "US Aquaculture Import-Export Analysis",
      description: "U.S. Aquaculture Trade & Market Opportunity Dashboard - A Tableau packaged workbook analyzing U.S. aquaculture imports, exports, species trends, trade partners, and consumption growth to identify market opportunities and trade patterns in the aquaculture industry.",
      icon: <FiTrendingUp />,
      techStack: ["Tableau", "Market Analysis", "Trade Data", "Data Visualization", "Industry Research"],
      demoLink: "https://github.com/Shardul1998/US-Aquaculture-Import-Export",
      githubLink: "https://github.com/Shardul1998/US-Aquaculture-Import-Export",
      category: "Market Research"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <ProjectsContainer>
      <Header>
        <Title
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Featured Projects
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          A collection of data analysis and business intelligence projects showcasing 
          SQL expertise, dashboard development, and market research capabilities.
        </Subtitle>
      </Header>

      <GitHubNote
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <p>
          <FiGithub />
          My projects are uploaded on <a href="https://github.com/Shardul1998" target="_blank" rel="noopener noreferrer">GitHub profile</a>
        </p>
      </GitHubNote>

      <ProjectsGrid>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ display: 'contents' }}
        >
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              variants={cardVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ProjectIcon>
                {project.icon}
              </ProjectIcon>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <TechStack>
                {project.techStack.map((tech, index) => (
                  <TechTag key={index}>{tech}</TechTag>
                ))}
              </TechStack>
              <ProjectLinks>
                <ProjectLink href={project.demoLink} target="_blank" rel="noopener noreferrer">
                  <FiExternalLink />
                  View Project
                </ProjectLink>
                <ProjectLink href={project.githubLink} target="_blank" rel="noopener noreferrer">
                  <FiGithub />
                  Code
                </ProjectLink>
              </ProjectLinks>
            </ProjectCard>
          ))}
        </motion.div>
      </ProjectsGrid>
    </ProjectsContainer>
  );
};

export default Projects;